import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { stripeAPI } from '../services/api';
import { toast } from 'react-hot-toast';
import { X, CreditCard } from 'lucide-react';

interface PaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  plan: {
    id: number;
    name: string;
    price_monthly: number;
    price_yearly: number;
    features: string;
  };
  onSuccess?: () => void;
}

const PaymentModal: React.FC<PaymentModalProps> = ({ 
  isOpen, 
  onClose, 
  plan
}) => {
  const { t } = useTranslation();
  const [billingPeriod, setBillingPeriod] = useState<'monthly' | 'yearly'>('monthly');
  const [loading, setLoading] = useState(false);

  if (!isOpen) return null;

  // Check if this is Unlimited plan (one-time payment)
  const isUnlimited = plan.id === 8;

  const handleCheckout = async () => {
    setLoading(true);
    try {
      const response = await stripeAPI.createCheckoutSession({
        planId: plan.id,
        billingPeriod: isUnlimited ? 'lifetime' : billingPeriod
      });

      if (response.data.success && response.data.sessionResult?.sessionUrl) {
        // Redirect to Stripe Checkout
        window.location.href = response.data.sessionResult.sessionUrl;
      } else {
        throw new Error(response.data.sessionError || 'Failed to create checkout session');
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || error.message || 'Failed to create checkout session');
      setLoading(false);
    }
  };

  const price = isUnlimited 
    ? plan.price_yearly // For Unlimited, price_yearly contains the one-time payment amount
    : (billingPeriod === 'yearly' ? plan.price_yearly : plan.price_monthly);
  
  const savings = !isUnlimited && billingPeriod === 'yearly' 
    ? Math.round((1 - (plan.price_yearly / 12) / plan.price_monthly) * 100)
    : 0;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg max-w-md w-full p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
            {t('payment.selectPlan')}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="mb-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
            PresentClic {plan.name}
          </h3>
          <p className="text-gray-600 dark:text-gray-400 text-sm">
            {plan.features}
          </p>
        </div>

        {/* Show different UI for Unlimited (one-time payment) vs subscription plans */}
        {isUnlimited ? (
          <div className="mb-6 p-4 border rounded-lg bg-gray-50 dark:bg-gray-700">
            <div className="flex justify-between items-center">
              <div>
                <p className="font-medium text-gray-900 dark:text-white">
                  {t('payment.lifetimeAccess')}
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {t('payment.oneTimePayment')}
                </p>
              </div>
              <span className="text-2xl font-bold text-gray-900 dark:text-white">
                {(price / 100).toFixed(2)} €
              </span>
            </div>
          </div>
        ) : (
          <div className="space-y-3 mb-6">
            <label className="flex items-center justify-between p-4 border rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
              <div className="flex items-center">
                <input
                  type="radio"
                  name="billingPeriod"
                  value="monthly"
                  checked={billingPeriod === 'monthly'}
                  onChange={() => setBillingPeriod('monthly')}
                  className="mr-3"
                />
                <div>
                  <p className="font-medium text-gray-900 dark:text-white">
                    {t('payment.monthly')}
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {t('payment.cancelAnytime')}
                  </p>
                </div>
              </div>
              <span className="font-semibold text-gray-900 dark:text-white">
                {(plan.price_monthly / 100).toFixed(2)} €/{t('payment.month')}
              </span>
            </label>

            <label className="flex items-center justify-between p-4 border rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
              <div className="flex items-center">
                <input
                  type="radio"
                  name="billingPeriod"
                  value="yearly"
                  checked={billingPeriod === 'yearly'}
                  onChange={() => setBillingPeriod('yearly')}
                  className="mr-3"
                />
                <div>
                  <p className="font-medium text-gray-900 dark:text-white">
                    {t('payment.yearly')}
                    {savings > 0 && (
                      <span className="ml-2 text-sm bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300 px-2 py-1 rounded">
                        {t('payment.save')} {savings}%
                      </span>
                    )}
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {(plan.price_yearly / 12 / 100).toFixed(2)} €/{t('payment.month')}
                  </p>
                </div>
              </div>
              <span className="font-semibold text-gray-900 dark:text-white">
                {(plan.price_yearly / 100).toFixed(2)} €/{t('payment.year')}
              </span>
            </label>
          </div>
        )}

        <div className="border-t pt-4 mb-6">
          <div className="flex justify-between items-center">
            <span className="text-gray-600 dark:text-gray-400">
              {t('payment.total')}:
            </span>
            <span className="text-2xl font-bold text-gray-900 dark:text-white">
              {(price / 100).toFixed(2)} €
            </span>
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
            {t('payment.vatIncluded')}
          </p>
        </div>

        <button
          onClick={handleCheckout}
          disabled={loading}
          className="w-full bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
        >
          {loading ? (
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
          ) : (
            <>
              <CreditCard className="mr-2 w-5 h-5" />
              {t('payment.proceedToPayment')}
            </>
          )}
        </button>

        <div className="mt-4 flex items-center justify-center space-x-4 text-xs text-gray-500 dark:text-gray-500">
          <span>{t('payment.securePayment')}</span>
          <span>•</span>
          <span>Powered by Stripe</span>
        </div>
      </div>
    </div>
  );
};

export default PaymentModal;